import React, { useState, useEffect, useRef } from 'react';
import {withRouter} from 'react-router'
import {Link} from 'react-router-dom';
import Badge from '@material-ui/core/Badge';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import LatestNotificationsContainer from '../Dashboard/Widgets/LatestNotifications/latest_notifications_container';
import axios from 'axios'
import NotificationsIcon from '@material-ui/icons/Notifications';
import { useSelector } from 'react-redux';
import WifiOffIcon from '@material-ui/icons/WifiOff';
import { online } from '../../util/user_util';
import fullLogo from '../../assets/Logos/FullLogo_Clean.png'
import upperLogo from '../../assets/Logos/SVS_UpperLogo.png';
import  AvarisHeaderLogo  from '../../assets/Logos/AVARIS_Logo_WithLetters_Fixed.png';

// Helper function to format time (MM:SS)
const formatTime = (totalSeconds) => { 
    if (totalSeconds === null || totalSeconds < 0) return "--:--";
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    // Only show hours if there are any
    if (hours > 0) {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    } else {
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
};

const PrimaryHeader = (props) => {
    const { currentUser, currentUserId, users, fetchIncomingNotifications, fetchAllUsers, updateUserNotificationRead, logout: propsLogout } = props;
    const userNotifications = props.notifications;
    const [isShowingUserOptions, setIsShowingUserOptions] = useState(false);
    const [isShowingNotifications, setIsShowingNotifications] = useState(false);
    const [selectedWorkInstructionId, setSelectedWorkInstructionId] = useState(null);
    const [isOnline, setIsOnline] = useState(true); // Assume online initially
    // A profile picture that failed to load, so the default icon shows instead.
    const [failedProfileImage, setFailedProfileImage] = useState(null);
    const onlineCheckIntervalRef = useRef(null); // Ref for the polling interval

    // --- Refresh Token Timer State (Re-added) ---
    const refreshTokenExp = useSelector(state => state.session.refreshTokenExp);
    const [refreshTokenRemainingTime, setRefreshTokenRemainingTime] = useState(null);
    const refreshIntervalRef = useRef(null);
    // --------------------------------

    let unreadNotificationsCount = 0;
    let unreadNotifications = [];

    if (userNotifications && userNotifications.length > 0) {
        unreadNotifications = []; // Reset the array first
        unreadNotificationsCount = 0; // Reset the count
        
        // Track processed IDs to prevent duplicates
        const processedIds = new Set();
        const twoWeeksAgo = new Date();
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);

        userNotifications.forEach(notification => {
            // Skip if we've already processed this notification
            const notificationDate = new Date(notification.createdAt);

            if (processedIds.has(notification._id)) return;
            processedIds.add(notification._id);
            
            // Skip if it's the user's own notification
            if (notification.user_id === currentUserId) return;
            
            // Parse the users string into an array
            let readByArr = [];
            try {
                readByArr = JSON.parse(notification.users || "[]");
            } catch {
                readByArr = [];
            }
            
            // If current user's ID is not in the parsed array, it's unread
            if (notificationDate >= twoWeeksAgo) {
                unreadNotifications.push(notification);
                unreadNotificationsCount += 1;
            }
            // if (!readByArr.includes(currentUser._id.toString())) {
            //     unreadNotifications.push(notification);
            //     unreadNotificationsCount += 1;
            // }
        });
    }

    // --- Online Status Polling Effect ---
    useEffect(() => {
        let isMounted = true;

        const performOnlineCheck = () => {
            online().then(isOnlineResult => {
                if (isMounted && isOnline !== isOnlineResult) {
                    setIsOnline(isOnlineResult);
                }
            }).catch(err => {
                console.error("[PrimaryHeader] Error during periodic online check:", err);
                if (isMounted && isOnline) { // Only set to offline if previously online
                    setIsOnline(false);
                }
            });
        };

        // Initial check
        performOnlineCheck();

        // Setup polling (e.g., every 12 seconds)
        onlineCheckIntervalRef.current = setInterval(performOnlineCheck, 12000);

        // Cleanup
        return () => {
            isMounted = false;
            if (onlineCheckIntervalRef.current) {
                clearInterval(onlineCheckIntervalRef.current);
            }
        };
    }, [isOnline]); // Re-run effect slightly if isOnline state changes (might be redundant but safe)
    // -----------------------------------

    useEffect(() => {
        const source = axios.CancelToken.source();

        if (currentUserId) { 
            
            fetchIncomingNotifications(currentUserId); 
            fetchAllUsers(source.token);
        }

        return () => {
            
            source.cancel('PrimaryHeader effect cleanup, cancelling fetchUsers.');
        };
        // SIMPLIFIED Dependency Array: Rely only on currentUserId changing
        // This assumes fetchIncomingNotifications/fetchAllUsers are stable references
    }, [currentUserId]); 

    // --- Refresh Token Timer Effect (Re-added) ---
    useEffect(() => {
        // Clear any existing interval when expiry time changes or component unmounts
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
        }
        setRefreshTokenRemainingTime(null); // Reset display on change

        if (refreshTokenExp) {
            const calculateRemaining = () => {
                const now = Date.now();
                const expiryTime = refreshTokenExp * 1000; // Convert expiry to ms
                const timeLeft = Math.max(0, Math.floor((expiryTime - now) / 1000)); // Remaining seconds

                setRefreshTokenRemainingTime(timeLeft);

                // Stop interval and potentially log out if time runs out while offline
                if (timeLeft <= 0) {
                    console.log("[PrimaryHeader] Refresh token timer expired.");
                    if (refreshIntervalRef.current) {
                        clearInterval(refreshIntervalRef.current);
                        refreshIntervalRef.current = null;
                    }
                    // --- Add Logout Logic --- >
                    if (!isOnline) { 
                        console.log("[PrimaryHeader] Refresh token timer expired while backend unreachable. Logging out.");
                        propsLogout(currentUserId); // Call the logout function passed via props
                    }
                    // --- End Logout Logic --- >
                }
            };

            calculateRemaining(); // Run once immediately

            // Only set interval if time is remaining after initial calculation
            const initialTimeLeft = Math.max(0, Math.floor((refreshTokenExp * 1000 - Date.now()) / 1000));
            if (initialTimeLeft > 0) {
                refreshIntervalRef.current = setInterval(calculateRemaining, 1000); // Update every second
            }
        }

        // Cleanup function for when component unmounts or refreshTokenExp changes
        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
        };
    }, [refreshTokenExp, isOnline, propsLogout, currentUserId]); // Rerun effect if expiry, online status, or logout function changes
    // ----------------------------------

    const toggleUserOptions = (e) => {
        e.preventDefault()
        setIsShowingUserOptions(!isShowingUserOptions)
        setIsShowingNotifications(false)        
    }

    if (props.location.state && !selectedWorkInstructionId) {
        const { WIID } = props.location.state
        setSelectedWorkInstructionId(WIID)
    }

    const toggleNotifications = (notificationsCount) => {
        const aboutToShow = !isShowingNotifications;
        
        setIsShowingUserOptions(false) // Close user options if open
        setIsShowingNotifications(aboutToShow); // Toggle the notification visibility

        // Only mark as read if there are unread notifications AND we are *closing* the dropdown
        if (unreadNotifications && notificationsCount > 0 && !aboutToShow) { 
            
            updateUserNotificationRead(currentUserId)
        }
    }

    // --- DEBUG LOG --- >
    

    return <div id="header-container" className="max-width">
    <div id="header-nav-info" className="flex">
       <div id="AVARIS-logo-container" className="flex-centered">
            {/* <svg xmlns="http://www.w3.org/2000/svg" width="56" height="49" viewBox="0 0 56 49" fill="none">
                <path d="M28 0.000118256L0 49H28V0.000118256Z" fill="#07A8FC" />
                <path d="M28 1.52588e-05L56 49H28V1.52588e-05Z" fill="#2B5DFC" />
                <path d="M28 28.0001L40 49H17L28 28.0001Z" fill="#081F5F" />
                <path d="M28.0001 33L36.335 49H20.1963L28.0001 33Z" fill="#2B5DFC" />
                <path d="M20.1914 49L28.0004 33L28.2608 49H20.1914Z" fill="#07A8FC" />
            </svg> */}
          <img src={AvarisHeaderLogo} style={{height: "50px"}} alt="AVARIS Logo" /> 
       </div>
        {/* <div className="flex-column" style={{marginLeft: "6px"}}>
            <h3 style={{ color: "#06a8fc" }}>AVARIS</h3>
            <h5 className="signature">Sharp Vision Software</h5>
        </div> */}
    </div>
    {/* <div id="logo-container">
        <img  style={{height: "50px"}} src={fullLogo} alt="AVARIS Logo" />
    </div> */}
    <div id="user-info-panel" className="flex-align-center">
        {/* Display Offline Icon Only */}
        {!isOnline && (
            <div style={{ color: 'orange', marginRight: '15px' }} title="Status: Offline">
                <WifiOffIcon />
            </div>
        )}

        {/* Display Refresh Token Timer Only When Offline */}
        {!isOnline && refreshTokenExp && (
            <div style={{ color: 'orange', marginRight: '15px', fontSize: '0.9em' }} title={`Offline Session Refresh Expires: ${new Date(refreshTokenExp * 1000).toLocaleString()}`}>
                Offline Session: {refreshTokenRemainingTime !== null && refreshTokenRemainingTime > 0
                                    ? formatTime(refreshTokenRemainingTime)
                                    : "Expired"}
            </div>
        )}

        {unreadNotificationsCount > 0 ? (
            <Badge badgeContent={unreadNotificationsCount} max={99} color="error">
                <NotificationsIcon 
                    className="clickable" 
                    style={{
                        color: "white", 
                        fontSize: '2em', 
                        position: 'relative'
                    }} 
                    onClick={() => toggleNotifications(unreadNotificationsCount)}
                />
            </Badge>
        ) : (
            <NotificationsIcon 
                className="clickable" 
                style={{
                    color: "white", 
                    fontSize: '2em', 
                    position: 'relative'
                }} 
                onClick={() => toggleNotifications(unreadNotificationsCount)}
            />
        )}
        {isShowingNotifications &&
            <div id="pop-up-notifications" className="flex-column">
                <LatestNotificationsContainer
                    fromBell={isShowingNotifications}
                    notifications={unreadNotifications}
                    toggleNotifications={() => toggleNotifications(unreadNotificationsCount)}
                    updateUserNotificationRead={updateUserNotificationRead}
                    currentUserId={currentUserId}
                    users={users} />
                <div id="pop-up-notifications-bottom-area"></div>
            </div>}
            <div> 
                {users[currentUserId] && users[currentUserId].image && users[currentUserId].image !== failedProfileImage ? 
                <img id="header-profile-image" className="clickable" src={users[currentUserId].image} alt="user profile" onClick={toggleUserOptions}
                onError={() => setFailedProfileImage(users[currentUserId].image)} 
                />
                : <AccountCircleIcon className="clickable" onClick={(e) => toggleUserOptions(e)} 
                style={{position: "relative", color: "white", fontSize: "3em", margin: '0 .5em'}} />
            }
                    {isShowingUserOptions &&
                        <div id="pop-up-options" className="bubble flex-column shadow">
                            <span id="username"><div>Username:</div><p>{currentUser.username}</p></span>
                            <span id="role"><div>Role:</div><p>{currentUser.role}</p></span>
                            <Link id="account-settings-button" className="clickable link-text" to="/dashboard/settings" onClick={(e) => setIsShowingUserOptions(false)}>Account Settings</Link>
                            <div id="logout-button" className="clickable link-text" onClick={() => propsLogout(currentUserId)}>Logout</div>

                    </div>}
  
            </div>
            <div id="logo-container">
                <img  style={{height: "30px"}} src={upperLogo} alt="AVARIS Logo" />
            </div>
    </div>
</div>
}

export default withRouter(PrimaryHeader)